/**
 * useRestaurantHours Composable
 * Provides logic for checking restaurant hours and closed status
 * Follows SOLID principles - Single Responsibility: handles only restaurant hours logic
 * Compatible with Vue 2 and Vue 3
 */

import moment from "moment-timezone";

/**
 * Checks if restaurant is currently closed based on dining option timings
 * @param {Object} diningOption - Current dining option with timings
 * @param {string} timezone - Store timezone
 * @param {Object} store - Store object with start_time/close_time fallback
 * @returns {boolean} True if restaurant is closed, false otherwise
 */
export function isRestaurantClosed(diningOption, timezone, store = null) {
  if (!diningOption || !timezone) {
    return false; // If we can't determine, assume open
  }

  const now = moment().tz(timezone);
  const todayDay = now.day() || 7; // moment.day() returns 0-6, we need 1-7

  // Check if dining option has specific timings
  if (diningOption.timings && diningOption.timings.length > 0) {
    // Find timings for today
    const timingsForToday = diningOption.timings.filter(
      (timing) => timing.day_of_week === todayDay && !timing.is_closed
    );

    // If no timings for today or all are closed, restaurant is closed
    if (timingsForToday.length === 0) {
      return true;
    }

    // Check if current time is within any open time slot
    const isWithinHours = timingsForToday.some((timing) => {
      const openTime = moment.tz(timing.opening_time, "HH:mm:ss", timezone);
      const closeTime = moment.tz(timing.closing_time, "HH:mm:ss", timezone);

      // Handle case where closing time is next day (e.g., 23:00 - 02:00)
      if (closeTime.isBefore(openTime)) {
        return now.isSameOrAfter(openTime) || now.isBefore(closeTime);
      }

      return now.isSameOrAfter(openTime) && now.isBefore(closeTime);
    });

    return !isWithinHours;
  }

  // Fallback to store hours if no dining option timings
  if (store && store.start_time && store.close_time) {
    const openTime = moment.tz(store.start_time, "HH:mm:ss", timezone);
    const closeTime = moment.tz(store.close_time, "HH:mm:ss", timezone);

    // Handle case where closing time is next day
    if (closeTime.isBefore(openTime)) {
      return !(now.isSameOrAfter(openTime) || now.isBefore(closeTime));
    }

    return !(now.isSameOrAfter(openTime) && now.isBefore(closeTime));
  }

  // If we can't determine, assume open
  return false;
}

/**
 * Gets formatted working hours string for display
 * @param {Object} diningOption - Current dining option with timings
 * @param {string} timezone - Store timezone
 * @param {Object} store - Store object with start_time/close_time fallback
 * @returns {string} Formatted hours string or "Not Available"
 */
export function getWorkingHours(diningOption, timezone, store = null) {
  if (!diningOption || !timezone) {
    return "Not Available";
  }

  const now = moment().tz(timezone);
  const todayDay = now.day() || 7;

  // Check if dining option has specific timings
  if (diningOption.timings && diningOption.timings.length > 0) {
    const timingsForToday = diningOption.timings.filter(
      (timing) => timing.day_of_week === todayDay && !timing.is_closed
    );

    if (timingsForToday.length > 0) {
      const slots = timingsForToday.map((timing) => {
        const openTime = moment.tz(timing.opening_time, "HH:mm:ss", timezone);
        const closeTime = moment.tz(timing.closing_time, "HH:mm:ss", timezone);
        return `${openTime.format("hh:mm A")} - ${closeTime.format("hh:mm A")}`;
      });

      return `Open from: ${slots.join(", ")}`;
    }

    // If closed today, show general hours for the week
    const allTimings = diningOption.timings.filter(
      (timing) => !timing.is_closed
    );
    if (allTimings.length > 0) {
      // Group by day and format
      const dayNames = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday"
      ];
      const hoursByDay = {};

      allTimings.forEach((timing) => {
        const dayName =
          dayNames[timing.day_of_week - 1] || `Day ${timing.day_of_week}`;
        const openTime = moment.tz(timing.opening_time, "HH:mm:ss", timezone);
        const closeTime = moment.tz(timing.closing_time, "HH:mm:ss", timezone);
        const timeSlot = `${openTime.format("hh:mm A")} - ${closeTime.format(
          "hh:mm A"
        )}`;

        if (!hoursByDay[dayName]) {
          hoursByDay[dayName] = [];
        }
        hoursByDay[dayName].push(timeSlot);
      });

      // Format as "Monday-Friday: 9:00 AM - 10:00 PM" or show individual days
      const dayKeys = Object.keys(hoursByDay);
      if (dayKeys.length === 1) {
        return `${dayKeys[0]}: ${hoursByDay[dayKeys[0]].join(", ")}`;
      }

      // Show first available day as example
      const firstDay = dayKeys[0];
      return `${firstDay}: ${hoursByDay[firstDay].join(", ")}`;
    }

    return "Closed Today";
  }

  // Fallback to store hours
  if (store && store.start_time && store.close_time) {
    const openTime = moment.tz(store.start_time, "HH:mm:ss", timezone);
    const closeTime = moment.tz(store.close_time, "HH:mm:ss", timezone);
    return `Open from ${openTime.format("hh:mm A")} - ${closeTime.format(
      "hh:mm A"
    )}`;
  }

  return "Not Available";
}

/**
 * Composable function for Vue 2 compatibility
 * Wraps the functions to work with Vue 2 reactive store
 * @param {Object} store - Vuex store instance
 * @returns {Object} Methods and computed values for restaurant hours
 */
export function useRestaurantHours(store) {
  if (!store) {
    console.warn("useRestaurantHours: Store instance is required");
    return {
      isClosed: () => false,
      getWorkingHours: () => "Not Available"
    };
  }

  /**
   * Checks if restaurant is currently closed
   * @returns {boolean} True if closed, false otherwise
   */
  function checkIsClosed() {
    const diningOptionId = store.getters.dining_option;
    const availableDiningOptions = store.state.availableDiningOptions || [];
    const diningOption = availableDiningOptions.find(
      (option) => option.id === diningOptionId
    );
    const timezone = store.getters.getStoreTimezone;
    const storeData = store.state.store;

    return isRestaurantClosed(diningOption, timezone, storeData);
  }

  /**
   * Gets formatted working hours
   * @returns {string} Formatted hours string
   */
  function getFormattedHours() {
    const diningOptionId = store.getters.dining_option;
    const availableDiningOptions = store.state.availableDiningOptions || [];
    const diningOption = availableDiningOptions.find(
      (option) => option.id === diningOptionId
    );
    const timezone = store.getters.getStoreTimezone;
    const storeData = store.state.store;

    return getWorkingHours(diningOption, timezone, storeData);
  }

  return {
    isClosed: checkIsClosed,
    getWorkingHours: getFormattedHours
  };
}
